import { useEffect, useState, useCallback } from 'react';
import api from '../../api/client';

function fmtDur(s) {
  if (!s || s <= 0) return '';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export default function QueuePanel({ open, onClose, onCount }) {
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState([]);
  const [playback, setPlayback] = useState(null);
  const [busy, setBusy] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/playback/queue');
      setQueue(data.queue || []);
      setHistory(data.history || []);
      setPlayback(data.playback || null);
      if (onCount) onCount((data.queue || []).length);
    } catch (e) {
      // ignore
    }
  }, [onCount]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [open, load]);

  const enqueue = async (mediaFileId) => {
    try {
      await api.post('/api/playback/enqueue', { media_file_id: mediaFileId });
      load();
    } catch (e) {
      console.error('[QueuePanel] Enqueue failed:', e);
    }
  };

  const send = async (action, body) => {
    setBusy(action + (body?.media_file_id || ''));
    try {
      await api.post(`/api/playback/${action}`, body || {});
      load();
    } catch (e) {
      console.error(`[QueuePanel] ${action} failed:`, e);
    } finally {
      setBusy(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const id = e.dataTransfer.getData('text/plain');
    if (id) enqueue(id);
  };

  const current = playback?.currentTrack || null;

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} aria-hidden="true" />
      )}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-md transform bg-dark-850 border-l border-dark-700 shadow-2xl transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center justify-between px-4 border-b border-dark-700">
          <h2 className="text-lg font-semibold text-dark-100">🎵 Próximos</h2>
          <button onClick={onClose} className="rounded-md p-2 text-dark-400 hover:text-dark-200" title="Cerrar">
            ✕
          </button>
        </div>

        <div className="h-[calc(100%-4rem)] overflow-y-auto p-4">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`rounded-lg border-2 border-dashed p-3 text-center text-sm transition-colors ${
              dragOver
                ? 'border-primary-500 bg-primary-900/20 text-primary-300'
                : 'border-dark-700 text-dark-500'
            }`}
          >
            {dragOver ? 'Soltá para agregar a Próximos' : 'Arrastrá acá canciones desde la Biblioteca de medios'}
          </div>

          {current && (
            <div className="mt-3 rounded-lg bg-dark-800 border border-primary-700/60 px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-dark-500">Sonando ahora</p>
              <p className="text-sm font-semibold text-primary-300 truncate">{current.title || '—'}</p>
              <p className="text-xs text-dark-300 truncate">
                {current.artist || ''}
                {current.program ? ` · ${current.program}` : ''}
              </p>
            </div>
          )}

          <div className="mt-3 space-y-1">
            {queue.length === 0 ? (
              <p className="py-8 text-center text-sm text-dark-500">La cola está vacía.</p>
            ) : (
              queue.map((item, i) => (
                <div key={`q-${i}`} className="flex items-center gap-2 rounded bg-dark-800/50 px-2 py-1.5">
                  <span className="text-xs text-dark-500">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs text-dark-200">{item.title || '—'}</p>
                    <p className="truncate text-[10px] text-dark-500">
                      {item.artist || ''}
                      {item.program ? ` · ${item.program}` : ''}
                    </p>
                  </div>
                  <span className="text-[10px] font-mono text-dark-400">{fmtDur(item.duration_seconds)}</span>
                  {item.media_file_id && (
                    <>
                      <button
                        type="button"
                        onClick={() => send('play', { media_file_id: item.media_file_id })}
                        disabled={busy === `play${item.media_file_id}`}
                        className="flex h-6 w-6 items-center justify-center rounded bg-dark-700 text-dark-200 hover:bg-primary-600 hover:text-white text-xs"
                        title="Reproducir ahora"
                      >
                        ▶
                      </button>
                      <button
                        type="button"
                        onClick={() => send('remove', { media_file_id: item.media_file_id })}
                        disabled={busy === `remove${item.media_file_id}`}
                        className="flex h-6 w-6 items-center justify-center rounded bg-dark-700 text-error-400 hover:bg-error-900/40 text-xs"
                        title="Quitar de la cola"
                      >
                        ✕
                      </button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>

          {history.length > 0 && (
            <div className="mt-5 border-t border-dark-700 pt-3">
              <p className="text-[10px] font-medium uppercase tracking-wide text-dark-500 px-1">
                ↑ Sonó recientemente
              </p>
              <div className="mt-1 space-y-1">
                {history.slice(0, 8).map((item, i) => (
                  <div key={`h-${i}`} className="flex items-center gap-2 rounded px-2 py-1">
                    <span className="text-xs text-dark-600">✔</span>
                    <p className="min-w-0 flex-1 truncate text-xs text-dark-500">{item.title || '—'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
